import React, { useState, useEffect, useMemo , useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next'; 
import OrderModal from './OrderModal2';
import ReviewCard from './ReviewCard';
import AddReview from './Review'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faChevronLeft, faStar, faQuoteLeft, faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons'; // ✅ Դատարկ սիրտը
import { API_BASE } from './apiConfig';
import { toast } from 'react-hot-toast'; // ✅ Եթե օգտագործում ես toast

const SubServicePage = () => {
    const { t, i18n } = useTranslation(); 
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [data, setData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [isFavorite, setIsFavorite] = useState(false); // ✅ Սիրված լինելու state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
const hasIncrementedView = useRef(false);
    const goldColor = '#c5a059';

    const user = useMemo(() => {
        const saved = localStorage.getItem('liber_user');
        try { return saved ? JSON.parse(saved) : null; } catch { return null; }
    }, []);

    const token = localStorage.getItem('token');

    // ✅ Սիրվածների ստուգում
    const checkFavoriteStatus = async (subserviceId) => {
        if (!user || !token) return;
        try {
            const res = await fetch(`${API_BASE}/api/favorites/${user.id}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'ngrok-skip-browser-warning': 'true' }
            });
            const favIds = await res.json();
            if (Array.isArray(favIds)) {
                setIsFavorite(favIds.some(favId => String(favId) === String(subserviceId)));
            }
        } catch (err) {
            console.error("Fav check error:", err);
        }
    };

    // ✅ Սիրտը սեղմելու ֆունկցիա
    const toggleFavorite = async (e) => {
        e.stopPropagation();
        if (!user) {
            toast.error(t('login_required_fav') || 'Please login to add to favorites');
            navigate('/user-login');
            return;
        }

        try {
            const res = await fetch(`${API_BASE}/api/favorites/toggle`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true'
                },
                body: JSON.stringify({ user_id: user.id, subservice_id: data.id })
            });
            const result = await res.json();
            if (result.success) {
                setIsFavorite(!isFavorite);
                toast.success(isFavorite ? t('removed_from_fav') : t('added_to_fav'));
            }
        } catch (err) {
            toast.error("Error toggling favorite");
        }
    };

const fetchData = async () => {
    setLoading(true);
    // Ամեն անգամ fetchData կանչելիս (երբ ID-ն փոխվում է), թույլ ենք տալիս նոր հաշվարկ
    hasIncrementedView.current = false; 

    try {
        const fetchHeaders = { 
            'ngrok-skip-browser-warning': 'true', 
            'Accept': 'application/json' 
        };

        // 1. Բեռնում ենք բոլոր ենթածառայությունները
        const res = await fetch(`${API_BASE}/api/subservices`, { headers: fetchHeaders });
        const allData = await res.json();

        // 2. Փնտրում ենք համապատասխան տվյալը ըստ ID-ի կամ SLUG-ի
        const found = allData.find(item => 
            String(item.id) === String(id) || String(item.slug) === String(id)
        );
        
        if (found) {
            setData(found);
            checkFavoriteStatus(found.id);

            // ✅ ԴԻՏՈՒՄԻ ԳՐԱՆՑՈՒՄ (INCREMENT VIEWS)
            // Օգտագործում ենք hasIncrementedView.current, որ StrictMode-ի պատճառով 2 անգամ չհաշվի
            if (!hasIncrementedView.current) {
                fetch(`${API_BASE}/api/stats/view-subservice/${found.id}`, { 
                    method: 'POST', 
                    headers: fetchHeaders 
                })
                .then(r => r.json())
                .then(resData => {
                    if (resData.success) {
                        hasIncrementedView.current = true; // Նշում ենք, որ արդեն հաշվվել է
                        console.log("Դիտումը հաջողությամբ գրանցվեց");
                    }
                })
                .catch(err => console.error("Stats increment error:", err));
            }

            // ✅ URL REDIRECT ՏՐԱՄԱԲԱՆՈՒԹՅՈՒՆ
            if (found.slug && !isNaN(id)) {
                navigate(`/subservice/${found.slug}`, { replace: true });
            }

            // 3. Բեռնում ենք ռեվյուները (Reviews)
            const revRes = await fetch(`${API_BASE}/api/reviews/subservice/${found.id}`, { headers: fetchHeaders });
            if (revRes.ok) {
                const revData = await revRes.json();
                setReviews(Array.isArray(revData) ? revData : []);
            }
        } else {
            setData(null);
        }
    } catch (err) {
        console.error("🔥 Detailed Fetch Error:", err);
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        window.scrollTo(0, 0);
        if (id) fetchData();
    }, [id]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getFullImgPath = (img) => {
        if (!img) return "https://via.placeholder.com/800x500?text=No+Image";
        if (img.startsWith('http')) return img;
        const path = img.startsWith('/') ? img : `/${img}`;
        return `${API_BASE}${path}`.replace(/([^:]\/)\/+/g, "$1");
    };

    const averageRating = useMemo(() => {
        if (reviews.length === 0) return "0.0";
        const sum = reviews.reduce((acc, curr) => acc + (Number(curr.stars) || 0), 0);
        return (sum / reviews.length).toFixed(1);
    }, [reviews]);

    if (loading) return (
        <div style={{height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff'}}>
            <FontAwesomeIcon icon={faCircleNotch} spin size="3x" color={goldColor} />
        </div>
    );

    if (!data) return (
        <div style={{padding: '150px 20px', textAlign: 'center'}}>
            <h2 style={{color: '#64748b'}}>{t('service_not_found')}</h2>
            <button onClick={() => navigate('/')} style={{marginTop: '20px', color: goldColor, border: 'none', background: 'none', cursor: 'pointer', fontWeight: 'bold'}}>
                {t('return_home')}
            </button>
        </div>
    );
 
    // --- Styles ---
    const pageWrapperStyle = { padding: isMobile ? '100px 15px 50px' : '120px 15px 50px', background: '#fafafa', minHeight: '100vh' };
    const containerStyle = { maxWidth: '900px', margin: '0 auto' };
    const contentGridStyle = { background: '#fff', borderRadius: isMobile ? '24px' : '32px', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', position: 'relative' };
    
    // ✅ Սրտիկի կոճակի ոճը
    const favBtnStyle = {
        position: 'absolute',
        top: '20px',
        right: '20px',
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        background: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10,
        border: 'none',
        transition: '0.3s'
    };

 return (
    <div style={pageWrapperStyle}>
      <Helmet defer={false}>
    <html lang={i18n.language || 'hy'} />
    
    {/* 1. Խուսափում ենք դատարկ վերնագրերից fetch-ի ժամանակ */}
    <title>
        {data ? `${data[`name_${i18n.language}`] || data.name} | Գինը և Պատվեր | LIBER Group` : "Բեռնափոխադրում և ծառայություններ Երևանում | LIBER"}
    </title>

    {/* 2. Ավելի հարուստ Description */}
    <meta name="description" content={
        data 
        ? (i18n.language === 'hy' 
            ? `Պատվիրեք ${data.name_hy || data.name} լավագույն գնով: Պրոֆեսիոնալ մոտեցում, արագ սպասարկում 24/7 Երևանում և մարզերում:` 
            : `Order ${data.name_en || data.name} at the best price in Armenia. Fast and professional service 24/7.`)
        : "Liber Group - Պրոֆեսիոնալ բեռնափոխադրում, աղբահանություն և բանվորական ուժ Երևանում:"
    } />

    {/* 3. ՃԻՇՏ CANONICAL - Սա կփակի Duplicate-ի հարցը */}
    {data?.slug && (
        <link rel="canonical" href={`https://liber.am/subservice/${data.slug}`} />
    )}

    {/* 4. Լեզվային կապեր (Hreflang) - Սա ասում է Google-ին, որ սա նույն էջի տարբեր լեզուներն են */}
    <link rel="alternate" href={`https://liber.am/hy/subservice/${data?.slug || id}`} hreflang="hy" />
    <link rel="alternate" href={`https://liber.am/en/subservice/${data?.slug || id}`} hreflang="en" />
    <link rel="alternate" href={`https://liber.am/subservice/${data?.slug || id}`} hreflang="x-default" />

    {/* 5. Robots - Եթե տվյալները չկան, թույլ մի տուր ինդեքսավորել */}
    {!data ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}

    <meta property="og:title" content={`${data?.[`name_${i18n.language}`] || data?.name} - LIBER Group`} />
    <meta property="og:type" content="product" />
    <meta property="og:url" content={`https://liber.am/subservice/${data?.slug || id}`} />
    {data?.image && <meta property="og:image" content={`${API_BASE}${data.image}`} />}

    {/* 6. Schema - Փոխեցի AggregateRating, որ աստղերը երևան Google-ում */}
    {data && (
        <script type="application/ld+json">
            {JSON.stringify({
                "@context": "https://schema.org/",
                "@type": "Product",
                "name": data[`name_${i18n.language}`] || data.name,
                "image": data.image ? `${API_BASE}${data.image}` : "https://liber.am/logo.png",
                "description": data.description || "",
                "brand": { "@type": "Brand", "name": "LIBER Group" },
                "offers": {
                    "@type": "Offer",
                    "url": `https://liber.am/subservice/${data.slug}`,
                    "priceCurrency": "AMD",
                    "price": data.price || "0",
                    "availability": "https://schema.org/InStock",
                    "priceValidUntil": "2026-12-31"
                },
                "aggregateRating": reviews.length > 0 ? {
                    "@type": "AggregateRating",
                    "ratingValue": averageRating,
                    "reviewCount": reviews.length
                } : undefined
            })}
        </script>
    )}
</Helmet>

        <div style={containerStyle}>
            <button onClick={() => navigate(-1)} style={backButtonStyle}>
                <FontAwesomeIcon icon={faChevronLeft} /> {t('back_btn')}
            </button>

            <div style={contentGridStyle}>
                <button style={favBtnStyle} onClick={toggleFavorite}>
                    <FontAwesomeIcon 
                        icon={isFavorite ? faHeartSolid : faHeartRegular} 
                        style={{ color: isFavorite ? '#ef4444' : '#64748b', fontSize: '20px' }} 
                    />
                </button>

                <div style={imageWrapperStyle}>
                    <img 
                        src={getFullImgPath(data.image)} 
                        alt={data[`name_${i18n.language}`] || data.name} 
                        style={imageStyle} 
                    />
                </div>

                <div style={infoWrapperStyle}>
                    <h1 style={{...titleStyle, fontSize: isMobile ? '24px' : '32px'}}>
                        {data[`name_${i18n.language}`] || data.name}
                    </h1>
                    
                    {/* ԱՅՍՏԵՂ ԴԻՐ ՀԵԼՄԵԹԻ ՄԻՋԻՑ ՀԱՆՎԱԾ ՏԵՔՍՏԵՐԸ */}
                    <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '15px' }}>
                        Ինչո՞ւ ընտրել մեր {data[`name_${i18n.language}`] || data.name} ծառայությունը
                    </h2>
                    <ul style={{ color: '#475569', fontSize: '15px', lineHeight: '1.8', marginBottom: '30px' }}>
                        <li>✅ {t('benefit_1', 'Արագ արձագանքում 24/7')}</li>
                        <li>✅ {t('benefit_2', 'Փորձառու և պրոֆեսիոնալ անձնակազմ')}</li>
                        <li>✅ {t('benefit_3', 'Ամենամատչելի սակագները Երևանում')}</li>
                    </ul>

                    {/* Մնացած ինֆոն... */}
                    <div style={ratingRowStyle}>
                        <div style={ratingBadgeStyle}>
                            <FontAwesomeIcon icon={faStar} /> {averageRating}
                        </div>
                        <span style={reviewCountStyle}>{reviews?.length || 0} {t('reviews_count')}</span>
                    </div>

                    <p style={descriptionStyle}>
                        {data[`description_${i18n.language}`] || data.description}
                    </p>
                    
                    <div style={priceRowStyle}>
                        <div style={{flex: '1 1 200px'}}>
                            <span style={priceLabelStyle}>{t('price_from')}</span>
                            <div style={priceTextStyle}>{data.price} <span style={{fontSize: '16px'}}>֏</span></div>
                        </div>
                        <button 
                            onClick={() => user ? setIsModalOpen(true) : navigate('/user-login')} 
                            style={orderBtnStyle}
                        >
                            {t('order_now_btn')}
                        </button>
                    </div>
                    
                    {/* Reviews Section */}
                    <div style={reviewsSectionStyle}>
                         <div style={reviewsHeaderStyle}>
                             <FontAwesomeIcon icon={faQuoteLeft} style={{color: goldColor, fontSize: '24px'}} />
                             <h3 style={reviewsTitleStyle}>{t('customer_reviews_title')}</h3>
                         </div>
                         <AddReview subserviceId={data.id} onReviewSuccess={fetchData} />
                         {reviews && reviews.length > 0 ? (
                             <div style={reviewsGridStyle}>
                                 {reviews.map((rev, idx) => <ReviewCard key={rev.id || idx} review={rev} />)}
                             </div>
                         ) : (
                             <div style={noReviewsStyle}>{t('no_reviews_yet')}</div>
                         )}
                    </div>
                </div>
            </div>
        </div>

        {isModalOpen && (
            <OrderModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                serviceName={data[`name_${i18n.language}`] || data.name} 
                servicePrice={data.price} 
                userId={user?.id}
            />
        )}
    </div>
);
};

// ... (Static Styles-ը պահիր նույնը, ինչ ունեիր)
const backButtonStyle = { border: 'none', background: 'none', cursor: 'pointer', marginBottom: '20px', fontWeight: '700', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' };
const imageWrapperStyle = { width: '100%', maxHeight: '500px', overflow: 'hidden', background: '#eee', position: 'relative' };
const imageStyle = { width: '100%', height: '100%', objectFit: 'cover' };
const titleStyle = { fontWeight: '800', marginBottom: '15px', color: '#0f172a' };
const infoWrapperStyle = { padding: '40px', boxSizing: 'border-box' }; // Ավելացրի padding-ը
const ratingRowStyle = { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' };
const ratingBadgeStyle = { background: '#c5a059', color: '#fff', padding: '6px 14px', borderRadius: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' };
const reviewCountStyle = { color: '#64748b', fontSize: '15px' };
const descriptionStyle = { lineHeight: '1.8', color: '#475569', marginBottom: '35px', fontSize: '17px', borderLeft: '4px solid #c5a059', paddingLeft: '20px' };
const priceRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0f172a', padding: '25px 35px', borderRadius: '24px', flexWrap: 'wrap', gap: '20px', width: '100%', boxSizing: 'border-box' };
const priceLabelStyle = { color: 'rgba(255,255,255,0.6)', fontSize: '13px' };
const priceTextStyle = { fontSize: '32px', fontWeight: '800', color: '#c5a059', wordBreak: 'break-word', lineHeight: '1.2' };
const orderBtnStyle = { padding: '16px 35px', background: '#c5a059', color: '#fff', border: 'none', borderRadius: '16px', cursor: 'pointer', fontWeight: '800', fontSize: '16px', transition: '0.3s' };
const reviewsSectionStyle = { marginTop: '60px', paddingTop: '40px', borderTop: '2px solid #f1f5f9' };
const reviewsHeaderStyle = { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' };
const reviewsTitleStyle = { fontSize: '22px', fontWeight: '800', color: '#0f172a' };
const reviewsGridStyle = { display: 'flex', flexDirection: 'column', gap: '20px' };
const noReviewsStyle = { padding: '30px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: '20px' };

export default SubServicePage;